import type { SuccessResult } from '@woovi/shared';
import { CLICK_PIX_BUTTON_VARIANT } from '@woovi/ui';
import { CUSTOMER_REGISTER_FIELD_ENUM } from '@woovi/utils';

import type { PluginTypeButtonParams } from '../params/PluginTypeParams.ts';

const parseAdditionalInfo = (
  data: string,
): SuccessResult<{ data: Array<{ key: string; value: string }> }> => {
  // Validate input format
  if (!data || typeof data !== 'string') {
    return {
      success: false,
      error: 'Invalid input: data must be a non-empty string',
    };
  }

  const result = data.split(',').reduce((acc, pair) => {
    const [key, value] = pair.split(':');

    if (!key || !value) {
      // eslint-disable-next-line
      console.warn(`Skipping invalid pair: "${pair}"`);

      return acc;
    }

    return [
      ...acc,
      {
        key,
        value,
      },
    ];
  }, []);

  return { success: true, data: result };
};

type ButtonRootPropsAdditionalInfo = {
  key: string;
  value: string;
};

type ButtonRootProps = {
  appID: string;
  value: number;
  additionalInfo?: ButtonRootPropsAdditionalInfo[];
  customerRegisterFields?: CUSTOMER_REGISTER_FIELD_ENUM[];
  variant: CLICK_PIX_BUTTON_VARIANT;
  correlationID?: string;
};

type GetButtonRootPropsSuccessResult = {
  success: true;
  props: ButtonRootProps;
};

type GetButtonRootPropsErrorResult = {
  success: false;
  error: string;
};

export type GetButtonRootPropsResult =
  | GetButtonRootPropsSuccessResult
  | GetButtonRootPropsErrorResult;

export const getButtonRootProps = (
  params: PluginTypeButtonParams,
): GetButtonRootPropsResult => {
  if (!params.appID) {
    return {
      success: false,
      error: 'data-appid is a required field',
    };
  }

  if (!params.value) {
    return {
      success: false,
      error: 'data-value is a required field',
    };
  }

  const value = Number(params.value);

  if (isNaN(value)) {
    return {
      success: false,
      error: 'data-value must be a number',
    };
  }

  if (value <= 0) {
    return {
      success: false,
      error: 'data-value must be greater than 0',
    };
  }

  if (!Number.isInteger(value)) {
    return {
      success: false,
      error: 'data-value must be an integer',
    };
  }

  if (params.additionalInfo) {
    const result = parseAdditionalInfo(params.additionalInfo);

    if (!result.success) {
      return {
        success: false,
        error: result.error,
      };
    }
  }

  if (params.customerData) {
    const customerDataRegex = new RegExp(/^(\w+)(,\w+)*$/);

    const isValidCustomerData = customerDataRegex.test(params.customerData);

    if (!isValidCustomerData) {
      return {
        success: false,
        error: 'data-customer-fields must be a comma separated list',
      };
    }

    const customerDataValues = params.customerData.split(',');

    const isCustomerDataValuesValid = customerDataValues.every((value) =>
      Object.values(
        CUSTOMER_REGISTER_FIELD_ENUM as Record<string, string>,
      ).includes(value.toUpperCase()),
    );

    if (!isCustomerDataValuesValid) {
      return {
        success: false,
        error: 'data-customer-fields contains an invalid value',
      };
    }
  }

  const getAdditionalInfo = (): {
    additionalInfo?: ButtonRootPropsAdditionalInfo[];
  } => {
    if (!params.additionalInfo) {
      return {};
    }

    const result = parseAdditionalInfo(params.additionalInfo);

    if (!result.success) {
      return {};
    }

    return {
      additionalInfo: result.data,
    };
  };

  const getCustomerRegisterFields = (): {
    customerRegisterFields?: CUSTOMER_REGISTER_FIELD_ENUM[];
  } => {
    if (!params.customerData) {
      return {};
    }

    const values = params.customerData
      .toUpperCase()
      .split(',') as CUSTOMER_REGISTER_FIELD_ENUM[];

    return {
      customerRegisterFields: [...values],
    };
  };

  const getVariant = (): CLICK_PIX_BUTTON_VARIANT => {
    if (!params.variant) {
      return CLICK_PIX_BUTTON_VARIANT.DEFAULT;
    }

    const isValidVariant = Object.values(
      CLICK_PIX_BUTTON_VARIANT as Record<string, string>,
    ).includes(params.variant.toUpperCase());

    if (!isValidVariant) {
      return CLICK_PIX_BUTTON_VARIANT.DEFAULT;
    }

    return params.variant.toUpperCase() as CLICK_PIX_BUTTON_VARIANT;
  };

  const getCorrelationID = (): { correlationID?: string } => {
    if (!params.correlationID) {
      return {};
    }

    const correlationID = params.correlationID.trim();

    if (!correlationID) {
      return {};
    }

    return { correlationID };
  };

  const props: ButtonRootProps = {
    appID: params.appID,
    value,
    ...getAdditionalInfo(),
    ...getCustomerRegisterFields(),
    ...getCorrelationID(),
    variant: getVariant(),
  };

  return {
    success: true,
    props,
  };
};
